import React, { useEffect, useState, useRef, useCallback } from 'react';
// Helper to detect mobile device
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../../static/styles/SpaceFootball.module.css';
import { submitGameExit } from '../../api/games';
import MessagePopup from '../../components/MessagePopup';
import spaceFootballLevels from '../../static/data/spaceFootballLevels.json';

import airCraftImg from '../../static/img/SpaceFootball/charactor_and_objects/aircraft2.png.avif';
import footballImg from '../../static/img/SpaceFootball/charactor_and_objects/football.png.avif';

// Player frames
import playerb_0 from '../../static/img/SpaceFootball/player_sprints/playerb_0.png.avif';
import playerb_1 from '../../static/img/SpaceFootball/player_sprints/playerb_1.png.avif';
import playerb_2 from '../../static/img/SpaceFootball/player_sprints/playerb_2.png.avif';
import playerb_3 from '../../static/img/SpaceFootball/player_sprints/playerb_3.png.avif';
import playerb_4 from '../../static/img/SpaceFootball/player_sprints/playerb_4.png.avif';

// Bot frames
import playera_0 from '../../static/img/SpaceFootball/player_sprints/playera_0.png.avif';
import playera_1 from '../../static/img/SpaceFootball/player_sprints/playera_1.png.avif';
import playera_2 from '../../static/img/SpaceFootball/player_sprints/playera_2.png.avif';
import playera_3 from '../../static/img/SpaceFootball/player_sprints/playera_3.png.avif';
import playera_4 from '../../static/img/SpaceFootball/player_sprints/playera_4.png.avif';

// Sounds
import hitSound from '../../static/audio/SpaceFootball/pew_pew.mp3';
import wallSound from '../../static/audio/SpaceFootball/hit2.mp3';
import goalSound from '../../static/audio/SpaceFootball/oof.mp3';
// import serveSound from '../../static/audio/SpaceFootball/serve.mp3';
import winSound from '../../static/audio/SpaceFootball/win.mp3';
import loseSound from '../../static/audio/SpaceFootball/oh-no.mp3';
const isMobile = () => typeof window !== 'undefined' && (window.innerWidth <= 600 || /Mobi|Android/i.test(navigator.userAgent));

const SpaceFootball = () => {
  const canvasRef = useRef(null);
  const gameStateRef = useRef({
    gameRunning: false,
    gameOver: false,
    paused: false,
    playerScore: 0,
    botScore: 0,
    difficulty: 16,
    winningPoint: 5,
    baseBallSpeed: 0.008,
    serveSide: null,
    botServeTimer: null,
    goalFlash: 0,
    timerRef: null,
    assets: {
      loaded: false,
      loadedCount: 0,
      totalAssets: 0
    }
  });

  const [showOverlay, setShowOverlay] = useState(true);
  const [overlayContent, setOverlayContent] = useState({
    title: 'SPACE FOOTBALL',
    winner: '',
    score: '',
    showStart: true,
    showRestart: false
  });
  const [countdown, setCountdown] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  const spaceFootballState = location.state;

  // Get level data from local JSON
  const getLevelData = (displayLevel) => {
    return spaceFootballLevels.find(level => level.level === displayLevel);
  };

  // Game objects refs
  const ballRef = useRef({
    x: 0, y: 0, r: 20,
    dx: 0, dy: 0,
    speed: 0,
    spin: 0, rotation: 0,
    inPlay: true,
    lastHitBy: null,
    img: new Image()
  });

  const playerRef = useRef({
    x: 0, y: 0, r: 35,
    lastX: 0, lastY: 0,
    animPhase: 0,
    frames: [],
    frameIndex: 0,
    frameTimer: 0,
    frameDelay: 4,
    currentAnim: "idle"
  });

  const botRef = useRef({
    x: 0, y: 0, r: 35,
    lastX: 0, lastY: 0,
    animPhase: 0,
    frames: [],
    frameIndex: 0,
    frameTimer: 0,
    frameDelay: 4,
    currentAnim: "idle",
    loopTimer: 0,
    lastBallY: 0
  });

  const starsRef = useRef([]);
  const aircraftRef = useRef({
    x: 0, y: 0, dx: 0.2,
    width: 80, height: 40,
    img: new Image()
  });

  const defenseDummiesRef = useRef([]);
  const soundsRef = useRef({});


  // Initialize sounds
  const initializeSounds = useCallback(() => {
    soundsRef.current.hit = new Audio(hitSound);
    soundsRef.current.wall = new Audio(wallSound);
    soundsRef.current.goal = new Audio(goalSound);
    // soundsRef.current.serve = new Audio(serveSound);
    soundsRef.current.win = new Audio(winSound);
    soundsRef.current.lose = new Audio(loseSound);
  }, []);

  const playSound = useCallback((soundName) => {
    if (soundsRef.current[soundName]) {
      soundsRef.current[soundName].currentTime = 0;
      soundsRef.current[soundName].play().catch(() => {});
    }
  }, []);

  // Scale helpers for responsive design
  const getScaleHelpers = useCallback((canvas) => {
    const BASE_W = 500;
    const BASE_H = 900;
    const W = canvas.width;
    const H = canvas.height;
    
    return {
      W, H, BASE_W, BASE_H,
      sx: (px) => px * (W / BASE_W),
      sy: (px) => px * (H / BASE_H)
    };
  }, []);

  // Initialize stars
  const initStars = useCallback((W, H, count = 200) => {
    const stars = [];
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * W,
        y: Math.random() * H,
        brightness: 1.2 + Math.random() * 1.2,
        size: 0.6 + Math.random() * 1.2,
        dx: (Math.random() - 0.5) * 0.2,
        dy: (Math.random() - 0.5) * 0.2
      });
    }
    starsRef.current = stars;
  }, []);


  const createCharacterFrames = useCallback((type) => {
    if (type === 'playerb') {
      return [playerb_0, playerb_1, playerb_2, playerb_3, playerb_4].map(src => {
        const img = new Image();
        img.src = src;
        return img;
      });
    } else if (type === 'playera') {
      return [playera_0, playera_1, playera_2, playera_3, playera_4].map(src => {
        const img = new Image();
        img.src = src;
        return img;
      });
    }
    return [];
  }, []);

  // Initialize defense dummies based on difficulty
  const initializeDummies = useCallback((difficulty, W, H, sx, sy) => {
    const dummies = [];
    let dummyCount = 0;

    if (difficulty > 8) dummyCount = 1;
    if (difficulty > 10) dummyCount = 2;
    if (difficulty > 13) dummyCount = 3;
    if (difficulty > 15) dummyCount = 4;

    const spacing = sx(80);
    const startX = W / 2 - ((dummyCount - 1) * spacing) / 2;

    for (let i = 0; i < dummyCount; i++) {
      dummies.push({
        x: startX + i * spacing,
        y: sy(280),
        r: sx(15)
      });
    }

    defenseDummiesRef.current = dummies;
  }, []);

  // Initialize game objects
  const initializeGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { W, H, sx, sy } = getScaleHelpers(canvas);
    const gameState = gameStateRef.current;

  // Initialize ball
  const ball = ballRef.current;
  ball.img = new window.Image();
  ball.img.src = footballImg;
  ball.x = W / 2;
  ball.y = H / 2;
  ball.r = sx(20);
  ball.speed = gameState.baseBallSpeed * W * (1 + (gameState.difficulty * 0.15));
  // Reduce ball speed on mobile devices
  if (isMobile()) {
    ball.speed *= 0.55;
  }
  ball.dx = 0;
  ball.dy = 0;
  ball.spin = 0;
  ball.rotation = 0;
  ball.inPlay = false;


  // Initialize player
  const player = playerRef.current;
  player.frames = createCharacterFrames('playerb');
  player.x = W / 2;
  player.y = H - sy(100);
  player.r = sx(35);
  player.lastX = player.x;
  player.lastY = player.y;

  // Initialize bot
  const bot = botRef.current;
  bot.frames = createCharacterFrames('playera');
  bot.x = W / 2;
  bot.y = sy(100);
  bot.r = sx(35);
  bot.lastX = bot.x;
  bot.lastY = bot.y;

  // Initialize aircraft
  const aircraft = aircraftRef.current;
  aircraft.img = new window.Image();
  aircraft.img.src = airCraftImg;
  aircraft.x = W * 0.1;
  aircraft.y = H * 0.65;

    // Initialize stars and dummies
    initStars(W, H);
    initializeDummies(gameState.difficulty, W, H, sx, sy);

    // Count total assets for loading
    const totalAssets = 1 + player.frames.length + bot.frames.length + 1; // ball + player frames + bot frames + aircraft
    gameState.assets.totalAssets = totalAssets;

  }, [getScaleHelpers, initStars, initializeDummies, createCharacterFrames]);

  // Resize canvas
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    
    if (gameStateRef.current.gameRunning) {
      initializeGame();
    }
  }, [initializeGame]);

  // Check collision between ball and player/bot
  const checkCollision = useCallback((player, ball, sx, sy, W) => {
    const dx = ball.x - player.x;
    const dy = ball.y - player.y;
    const dist = Math.hypot(dx, dy);
    
    if (dist < player.r + ball.r) {
      const angle = Math.atan2(dy, dx);
      const hitFactor = dx / player.r;
      const speed = ball.speed + 0.8 * (W / 500);
      
      ball.dx = speed * Math.cos(angle + hitFactor * 0.3);
      ball.dy = speed * Math.sin(angle + hitFactor * 0.3);
      ball.spin = hitFactor * 0.15;
      ball.inPlay = true;
      
      ball.lastHitBy = (player === playerRef.current) ? "player" : "bot";
      playSound("hit");
    }
  }, [playSound]);

  // Bot AI
  const updateBotAI = useCallback((ball, bot, difficulty, W, H, sy, sx) => {
    const reaction = 0.015 + (difficulty * 0.01);
    let targetX = ball.x;

    // Difficulty-based behavior
    if (difficulty <= 3) {
      targetX += (Math.random() - 0.5) * sx(120 - difficulty * 20);
      if (Math.random() < 0.01 * (4 - difficulty)) return;
    } else if (difficulty <= 6) {
      targetX += (Math.random() - 0.5) * sx(80 - difficulty * 10);
      if (Math.random() < 0.005 * (7 - difficulty)) return;
    } else {
      targetX += (Math.random() - 0.5) * sx(30 - difficulty * 2);
    }

    // Loop detection
    if (!bot.loopTimer) bot.loopTimer = 0;
    if (!bot.lastBallY) bot.lastBallY = ball.y;

    if (Math.abs(ball.y - bot.lastBallY) < 5 && ball.y < H / 2) {
      bot.loopTimer++;
    } else {
      bot.loopTimer = 0;
    }
    bot.lastBallY = ball.y;

    let forceAdvance = false;
    if (bot.loopTimer > 60) {
      forceAdvance = true;
      bot.loopTimer = 0;
    }

    // Positioning
    const homeY = H / 8;
    const ballApproaching = ball.dy < 0;

    if (forceAdvance) {
      bot.y += 2.5;
    } else if (!ballApproaching) {
      bot.y += (homeY - bot.y) * 0.05;
    } else {
      bot.y += (homeY - bot.y) * 0.02;
    }

    // Apply movement
    targetX = Math.max(bot.r, Math.min(W - bot.r, targetX));
    bot.lastX = bot.x;
    bot.lastY = bot.y;
    bot.x += (targetX - bot.x) * reaction;
    bot.y = Math.min(H / 2 - bot.r, Math.max(bot.r, bot.y));
  }, []);

  // Reset ball after goal
  const resetBall = useCallback((scoredBy) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { W, H } = getScaleHelpers(canvas);
    const ball = ballRef.current;
    const gameState = gameStateRef.current;

    ball.dx = 0;
    ball.dy = 0;
    ball.spin = 0;
    ball.inPlay = false;

    if (gameState.botServeTimer) {
      clearTimeout(gameState.botServeTimer);
    }

  gameState.goalFlash = 12; // revert to previous flash duration
    playSound("goal");

    if (scoredBy === "player") {
      ball.x = W / 2;
      ball.y = H / 4;
      gameState.serveSide = "bot";
      gameState.botServeTimer = setTimeout(() => {
        if (!gameState.gameOver) {
          ball.dy = ball.speed;
          ball.inPlay = true;
          playSound("serve");
        }
      }, 1000);
    } else {
      ball.x = W / 2;
      ball.y = 3 * H / 4;
      gameState.serveSide = "player";
    }
  }, [getScaleHelpers, playSound]);

  // Check for win condition
  const checkWin = useCallback(() => {
    const gameState = gameStateRef.current;
    
    if (gameState.playerScore >= gameState.winningPoint || gameState.botScore >= gameState.winningPoint) {
      gameState.gameOver = true;
      gameState.gameRunning = false;
      
      const isWin = gameState.playerScore > gameState.botScore;
      setOverlayContent({
        title: 'MATCH RESULT',
        winner: isWin ? 'YOU WIN!' : 'YOU LOSE',
        score: `YOU ${gameState.playerScore} - ${gameState.botScore} OPPONENT`,
        showStart: false,
        showRestart: true
      });
      setShowOverlay(true);
      
      playSound(isWin ? "win" : "lose");

      // Submit game result if coming from ESL challenges
      if (spaceFootballState) {
        submitGameExit({
          game_unique_id: spaceFootballState.game_unique_id,
          game_name: spaceFootballState.game_name,
          game_level: spaceFootballState.game_level,
          level_credits: spaceFootballState.level_credits,
          is_passed: isWin ? "1" : "0"
        }).then(() => {
          const displayLevel = spaceFootballState.game_level;
          if (isWin) {
            setPopupMessage(`Level ${displayLevel} completed! You earned ${spaceFootballState.level_credits} credits.`);
          } else {
            setPopupMessage(`Level ${displayLevel} failed! Try again for ${spaceFootballState.level_credits} credits.`);
          }
          setPopupOpen(true);
        }).catch(() => {
          setPopupMessage(isWin ? 'Match completed!' : 'Try again!');
          setPopupOpen(true);
        });
      }
    }
  }, [playSound, spaceFootballState]);

  // Animate character frames
  const animateCharacter = useCallback((character) => {
    const moving = Math.abs(character.x - character.lastX) > 1 || Math.abs(character.y - character.lastY) > 1;

    if (moving) {
      character.frameTimer++;
      if (character.frameTimer >= character.frameDelay) {
        character.frameTimer = 0;
        character.frameIndex = (character.frameIndex + 1) % character.frames.length;
      }
    } else {
      character.frameIndex = 0; // idle frame
    }
  }, []);

  // Main game update loop
  const updateGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gameState = gameStateRef.current;
    if (gameState.gameOver || gameState.paused || !gameState.gameRunning) return;

    const { W, H, sx, sy } = getScaleHelpers(canvas);
    const ball = ballRef.current;
    const player = playerRef.current;
    const bot = botRef.current;

    // Update ball physics
    if (ball.inPlay) {
      ball.x += ball.dx + ball.spin * ball.dy * 0.2;
      ball.y += ball.dy - ball.spin * ball.dx * 0.2;
      ball.rotation += ball.spin * 10;
    }

    // Wall collisions
    if (ball.x - ball.r < sx(10) || ball.x + ball.r > W - sx(10)) {
      ball.dx *= -1;
      ball.spin *= -1;
      ball.x = Math.max(ball.r + sx(10), Math.min(W - ball.r - sx(10), ball.x));
      playSound("wall");
    }

    // Goal detection
    const goalWidth = sx(170);
    if (ball.y - ball.r < sy(10)) {
      if (ball.x > (W / 2 - goalWidth / 2) && ball.x < (W / 2 + goalWidth / 2)) {
        gameState.playerScore++;
        checkWin();
        if (!gameState.gameOver) resetBall("player");
      } else {
        ball.dy *= -1;
        ball.spin *= -1;
        playSound("wall");
      }
    }

    if (ball.y + ball.r > H - sy(10)) {
      if (ball.x > (W / 2 - goalWidth / 2) && ball.x < (W / 2 + goalWidth / 2)) {
        gameState.botScore++;
        checkWin();
        if (!gameState.gameOver) resetBall("bot");
      } else {
        ball.dy *= -1;
        ball.spin *= -1;
        playSound("wall");
      }
    }

    // Check collisions
    checkCollision(player, ball, sx, sy, W);
    checkCollision(bot, ball, sx, sy, W);

    // Defense dummies collision
    if (defenseDummiesRef.current.length > 0 && ball.lastHitBy === "player") {
      defenseDummiesRef.current.forEach(dummy => {
        const dx = ball.x - dummy.x;
        const dy = ball.y - dummy.y;
        const dist = Math.hypot(dx, dy);

        if (dist < dummy.r + ball.r) {
          const angle = Math.atan2(dy, dx);
          ball.dx = ball.speed * Math.cos(angle);
          ball.dy = ball.speed * Math.sin(angle);
          ball.spin *= -0.5;
          playSound("wall");
        }
      });
    }

    // Update bot AI
    updateBotAI(ball, bot, gameState.difficulty, W, H, sy, sx);

    // Animate characters
    animateCharacter(player);
    animateCharacter(bot);

    // Update effects
    if (gameState.goalFlash > 0) gameState.goalFlash--;
  }, [getScaleHelpers, checkCollision, updateBotAI, animateCharacter, playSound, checkWin, resetBall]);

  // Draw functions
  const drawField = useCallback((ctx, W, H, sx, sy, time = 0) => {
    // Cosmic background gradient
    const gradient = ctx.createRadialGradient(W / 2, H / 2, 100, W / 2, H / 2, W);
    gradient.addColorStop(0, "#1a0033");
    gradient.addColorStop(0.5, "#0b0030");
    gradient.addColorStop(1, "#050010");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, W, H);

    // Moving stars
    starsRef.current.forEach((s, i) => {
      s.x += s.dx;
      s.y += s.dy;
      if (s.x < 0) s.x = W;
      if (s.x > W) s.x = 0;
      if (s.y < 0) s.y = H;
      if (s.y > H) s.y = 0;
      const twinkle = 0.5 + 0.5 * Math.sin((time + i * 50) / 500);
      ctx.fillStyle = `rgba(255, ${180 + (i % 75)}, 255, ${s.brightness * twinkle})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
      ctx.fill();
    });

  // Nebula glow (no pulsing)
  const nebulaGradient = ctx.createLinearGradient(0, 0, W, H);
  nebulaGradient.addColorStop(0, "rgba(0, 200, 255, 0.12)");
  nebulaGradient.addColorStop(0.5, "rgba(200, 0, 255, 0.22)");
  nebulaGradient.addColorStop(1, "rgba(255, 100, 50, 0.1)");
  ctx.fillStyle = nebulaGradient;
  ctx.fillRect(0, 0, W, H);

    // Subtle cosmic haze
    ctx.fillStyle = "rgba(255,255,255,0.03)";
    ctx.beginPath();
    ctx.arc(W / 2, H / 2, W * 0.6, 0, Math.PI * 2);
    ctx.fill();

    // Center divider
    ctx.strokeStyle = "#0ff";
    ctx.shadowColor = "#0ff";
    ctx.shadowBlur = 20;
    ctx.lineWidth = sy(3);
    ctx.beginPath();
    ctx.moveTo(0, H / 2);
    ctx.lineTo(W, H / 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Solar system animation (slower rotation)
    ctx.save();
    ctx.translate(W / 2, H / 2);
    ctx.rotate(time / 12000); // slower rotation
    ctx.strokeStyle = "rgba(200,200,255,0.4)";
    for (let r = 40; r <= 120; r += 40) {
      ctx.beginPath();
      ctx.arc(0, 0, sx(r), 0, Math.PI * 2);
      ctx.stroke();
    }
  // Sun (no glow)
  ctx.beginPath();
  ctx.arc(0, 0, sx(14), 0, Math.PI * 2);
  ctx.fillStyle = "#ffdd88";
  ctx.fill();
  let planets = [
    { r: 40, size: 5, color: "#6cf", speed: 0.001 },
    { r: 80, size: 7, color: "#f77", speed: 0.0007 },
    { r: 120, size: 6, color: "#6f6", speed: 0.0005 }
  ];
    planets.forEach((p, idx) => {
      let angle = time * p.speed + idx * 2;
      let x = Math.cos(angle) * sx(p.r);
      let y = Math.sin(angle) * sy(p.r);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(x, y, sx(p.size), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      ctx.beginPath();
      ctx.arc(x, y, sx(p.size * 2), 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

  // Shooting star (classic appearance, much less frequent, with cooldown)
  const shootingStarCycle = 7000; // faster star animation
  const shootingStarCooldown = 10000; // cooldown after star finishes
  if (!drawField.starState) {
    drawField.starState = { lastEnd: 0, active: false };
  }
  // If not active and enough time has passed, start a new star
  if (!drawField.starState.active && time - drawField.starState.lastEnd > shootingStarCooldown) {
    // Pick a random edge: 0=left, 1=top, 2=right, 3=bottom
    const edge = Math.floor(Math.random() * 4);
    let x0, y0, x1, y1;
    if (edge === 0) { x0 = 0; y0 = Math.random() * H; x1 = W; y1 = Math.random() * H; }
    else if (edge === 1) { x0 = Math.random() * W; y0 = 0; x1 = Math.random() * W; y1 = H; }
    else if (edge === 2) { x0 = W; y0 = Math.random() * H; x1 = 0; y1 = Math.random() * H; }
    else { x0 = Math.random() * W; y0 = H; x1 = Math.random() * W; y1 = 0; }
    drawField.starParams = {
      x0, y0, x1, y1,
      length: 40 + Math.random() * 20,
      headRadius: 4 + Math.random() * 2, // smaller head
      startTime: time
    };
    drawField.starState.active = true;
  }
  // If active, animate the star
  if (drawField.starState.active && drawField.starParams) {
    let progress = (time - drawField.starParams.startTime) / shootingStarCycle;
    if (progress >= 1) {
      drawField.starState.active = false;
      drawField.starState.lastEnd = time;
      drawField.starParams = null;
    } else {
      let sxStar = drawField.starParams.x0 + (drawField.starParams.x1 - drawField.starParams.x0) * progress;
      let syStar = drawField.starParams.y0 + (drawField.starParams.y1 - drawField.starParams.y0) * progress;
      // Direction for tail
      let dx = drawField.starParams.x1 - drawField.starParams.x0;
      let dy = drawField.starParams.y1 - drawField.starParams.y0;
      let angle = Math.atan2(dy, dx);
      ctx.save();
      ctx.translate(sxStar, syStar);
      ctx.rotate(angle);
      // Draw tail (no glow)
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.moveTo(-i * drawField.starParams.length / 8, 0);
        ctx.lineTo(-(i + 1) * drawField.starParams.length / 8, 0);
        ctx.strokeStyle = `rgba(255,255,255,${0.18 - i * 0.02})`;
        ctx.lineWidth = 3 - i * 0.3;
        ctx.shadowBlur = 0;
        ctx.stroke();
      }
      // Draw head (no glow, no alpha breathing)
      ctx.beginPath();
      ctx.arc(0, 0, drawField.starParams.headRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.shadowBlur = 0;
      ctx.fill();
      ctx.restore();
    }
  }

    // Aircraft
    const aircraft = aircraftRef.current;
    if (aircraft.img.complete) {
      aircraft.x += aircraft.dx;
      if (aircraft.x > W - aircraft.width || aircraft.x < 0) {
        aircraft.dx *= -1;
      }
      ctx.drawImage(aircraft.img, aircraft.x, aircraft.y, aircraft.width, aircraft.height);
    }

    // Goals with net
    const drawGoal = (x, y, width, depth, isTop = true) => {
      ctx.save();
      ctx.strokeStyle = "#ff0";
      ctx.shadowColor = "#ff0";
      ctx.shadowBlur = 25;
      ctx.lineWidth = sy(3);
      // Posts
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + (isTop ? depth : -depth));
      ctx.moveTo(x + width, y);
      ctx.lineTo(x + width, y + (isTop ? depth : -depth));
      ctx.stroke();
      // Crossbar
      ctx.beginPath();
      ctx.moveTo(x, y + (isTop ? depth : -depth));
      ctx.lineTo(x + width, y + (isTop ? depth : -depth));
      ctx.stroke();
      // Net
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(255,255,150,0.3)";
      ctx.shadowBlur = 0;
      let step = 12;
      for (let nx = x; nx <= x + width; nx += step) {
        ctx.beginPath();
        ctx.moveTo(nx, y);
        ctx.lineTo(nx, y + (isTop ? depth : -depth));
        ctx.stroke();
      }
      for (let ny = 0; ny <= depth; ny += step) {
        ctx.beginPath();
        ctx.moveTo(x, y + (isTop ? ny : -ny));
        ctx.lineTo(x + width, y + (isTop ? ny : -ny));
        ctx.stroke();
      }
      ctx.restore();
    };
    drawGoal(W / 2 - sx(70), 0, sx(140), sy(40), true);
    drawGoal(W / 2 - sx(70), H, sx(140), sy(40), false);
  }, []);

  const drawFootball = useCallback((ctx, ball) => {
    if (!ball.img.complete) return;

    ctx.save();
    ctx.translate(ball.x, ball.y);
    ctx.rotate(ball.rotation * Math.PI / 180);
    const scale = (ball.r * 2) / ball.img.width;
    ctx.drawImage(
      ball.img,
      -ball.img.width * scale / 2,
      -ball.img.height * scale / 2,
      ball.img.width * scale,
      ball.img.height * scale
    );
    ctx.restore();
  }, []);

  const drawCharacter = useCallback((ctx, character) => {
    if (!character.frames.length) return;
    
    const frame = character.frames[character.frameIndex];
    if (!frame || !frame.complete) return;

    const scale = (character.r * 1.6) / frame.width;
    ctx.drawImage(
      frame,
      character.x - (frame.width * scale) / 2,
      character.y - (frame.height * scale) / 2,
      frame.width * scale,
      frame.height * scale
    );
  }, []);

  const drawDefenseDummies = useCallback((ctx, sy, sx) => {
    if (defenseDummiesRef.current.length === 0) return;

    ctx.save();
    defenseDummiesRef.current.forEach(dummy => {
      const r = dummy.r;
      const x = dummy.x;
      const y = dummy.y;
      const floatOffset = Math.sin(Date.now() / 500 + x) * sy(r * 0.3);

      // Aura glow
      ctx.fillStyle = "rgba(255, 255, 0, 0.05)";
      ctx.beginPath();
      ctx.arc(x, y - sy(r * 0.5) + floatOffset, sx(r * (1.8 + 0.2 * Math.sin(Date.now() / 700))), 0, Math.PI * 2);
      ctx.fill();

      // Main eyeball
      ctx.fillStyle = "#ff0";
      ctx.shadowColor = "#ff0";
      ctx.shadowBlur = 25;
      ctx.beginPath();
      ctx.arc(x, y - sy(r * 0.5) + floatOffset, sx(r * 1.2), 0, Math.PI * 2);
      ctx.fill();

      // Inner pupil (pulsating)
      ctx.fillStyle = "#f0f";
      const pupilScale = 0.4 + 0.1 * Math.sin(Date.now() / 300);
      ctx.beginPath();
      ctx.arc(x, y - sy(r * 0.5) + floatOffset, sx(r * pupilScale), 0, Math.PI * 2);
      ctx.fill();

      // Arms / tentacle-like energy beams (animated)
      ctx.strokeStyle = "#0ff";
      ctx.shadowColor = "#0ff";
      ctx.shadowBlur = 20;
      ctx.lineWidth = sx(1.5);
      for (let a = 0; a < Math.PI * 2; a += Math.PI / 4) {
        ctx.beginPath();
        ctx.moveTo(x, y + floatOffset);
        for (let d = 1; d <= 10; d++) {
          let progress = d / 10;
          let wave = Math.sin(progress * 6 + Date.now() / 300 + a) * sy(r * 0.2);
          ctx.lineTo(
            x + Math.cos(a) * sx(r * 2 * progress) + wave,
            y + Math.sin(a) * sy(r * 2 * progress) + floatOffset + wave
          );
        }
        ctx.stroke();
      }
    });
    ctx.restore();
  }, []);

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { W, H, sx, sy } = getScaleHelpers(canvas);
    const gameState = gameStateRef.current;
    const ball = ballRef.current;
    const player = playerRef.current;
    const bot = botRef.current;

    ctx.clearRect(0, 0, W, H);
    
    drawField(ctx, W, H, sx, sy, Date.now());

    // Goal flash effect (less intense and shorter)
    if (gameState.goalFlash > 0) {
      ctx.fillStyle = `rgba(255,255,0,${0.18 * (gameState.goalFlash / 12)})`;
      ctx.fillRect(0, 0, W, H);
    }

    // Draw game objects
    drawDefenseDummies(ctx, sy, sx);
    drawFootball(ctx, ball);
    drawCharacter(ctx, player);
    drawCharacter(ctx, bot);

    // Draw UI
    ctx.fillStyle = "#0ff";
    ctx.shadowBlur = 10;
    ctx.shadowColor = "#0ff";
    ctx.font = `${sx(40)}px 'Share Tech Mono'`;
    ctx.fillText(gameState.playerScore, sx(20), H - sy(30));
    ctx.fillText(gameState.botScore, sx(20), sy(50));
    ctx.font = `${sx(20)}px 'Share Tech Mono'`;
    
    // Show display level instead of difficulty when coming from ESL
    const displayLevel = spaceFootballState ? 
      parseInt(spaceFootballState.game_level) : 
      (gameState.difficulty - 4); // Convert difficulty back to display level
    ctx.fillText("level: " + displayLevel, W - sx(120), H - sy(60));
    ctx.fillText("Target: " + gameState.winningPoint, W - sx(120), H - sy(20));
    ctx.shadowBlur = 0;

    // Serve indicator
    if (!ball.inPlay && !gameState.gameOver) {
      ctx.fillStyle = "#fff";
      ctx.font = `${sx(20)}px 'Share Tech Mono'`;
      if (gameState.serveSide === "player") {
        ctx.fillText("Your serve!", W / 2 - sx(50), H - sy(300));
      } else {
        ctx.fillText("Opponent serve.", W / 2 - sx(70), sy(300));
      }
    }

    // Always draw pause/play button in top right
    ctx.save();
    ctx.translate(W - sx(40), sy(40));
    ctx.fillStyle = "#0ff";
    ctx.shadowBlur = 15;
    ctx.shadowColor = "#0ff";
    if (gameState.paused) {
      // Draw play icon
      ctx.beginPath();
      ctx.moveTo(-6, -10);
      ctx.lineTo(10, 0);
      ctx.lineTo(-6, 10);
      ctx.closePath();
      ctx.fill();
    } else {
      // Draw pause icon
      ctx.fillRect(-8, -10, 5, 20);
      ctx.fillRect(3, -10, 5, 20);
    }
    ctx.shadowBlur = 0;
    ctx.restore();

    // Pause overlay
    if (gameState.paused && !gameState.gameOver) {
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#fff";
      ctx.font = `${sx(20)}px 'Share Tech Mono'`;
      ctx.fillText("PAUSED", W / 2 - sx(30), H / 2 + sy(20));
    }
  }, [getScaleHelpers, drawField, drawDefenseDummies, drawFootball, drawCharacter]);

  // Game loop
  const gameLoop = useCallback(() => {
    updateGame();
    render();
    if (gameStateRef.current.gameRunning) {
      gameStateRef.current.animationFrame = requestAnimationFrame(gameLoop);
    }
  }, [updateGame, render]);

  // Mouse/touch controls
  const handleCanvasMove = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || gameStateRef.current.gameOver || gameStateRef.current.paused) return;

    const rect = canvas.getBoundingClientRect();
    const { W, H } = getScaleHelpers(canvas);
    const mx = (clientX - rect.left) * (W / rect.width);
    const my = (clientY - rect.top) * (H / rect.height);
    const player = playerRef.current;

    if (my > H / 2 + player.r) {
      player.lastX = player.x;
      player.lastY = player.y;
      player.x = Math.max(player.r, Math.min(W - player.r, mx));
      player.y = Math.max(H / 2 + player.r, Math.min(H - player.r, my));
    }
  }, [getScaleHelpers]);

  const handleMouseMove = useCallback((e) => {
    handleCanvasMove(e.clientX, e.clientY);
  }, [handleCanvasMove]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleCanvasMove(touch.clientX, touch.clientY);
    }
  }, [handleCanvasMove]);

  // Toggle pause
  const togglePause = useCallback(() => {
    if (!gameStateRef.current.gameOver) {
      gameStateRef.current.paused = !gameStateRef.current.paused;
    }
  }, []);

  // Start countdown
  const startCountdown = useCallback(() => {
    setOverlayContent(prev => ({ ...prev, showStart: false, showRestart: false }));
    
    let count = 3;
    setCountdown(count);
    
    const timer = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(timer);
        setCountdown(0);
        setShowOverlay(false);
        startGame();
      }
    }, 1000);
  }, []);

  // Start game
  const startGame = useCallback(() => {
    const gameState = gameStateRef.current;
    gameState.playerScore = 0;
    gameState.botScore = 0;
    gameState.gameOver = false;
    gameState.paused = false;
    gameState.gameRunning = true;

    const canvas = canvasRef.current;
    if (canvas) {
      const { W, H } = getScaleHelpers(canvas);
      const ball = ballRef.current;
      
      ball.dx = 0;
      ball.dy = 0;
      ball.spin = 0;
      ball.inPlay = false;
      ball.x = W / 2;
      ball.y = 3 * H / 4;
      gameState.serveSide = "player";
    }

    gameLoop();
  }, [getScaleHelpers, gameLoop]);

  // Restart game
  const restartGame = useCallback(() => {
    startCountdown();
  }, [startCountdown]);

  // Handle popup close
  const handlePopupClose = () => {
    setPopupOpen(false);
    if (spaceFootballState) {
      navigate('/daily-gaming-tasks');
    }
  };

  // Initialize component
  useEffect(() => {
    document.title = 'Space Football | Ufone ESG';
    
    // Check if coming from ESL games and redirect if no state
    if (!spaceFootballState) {
      // Allow direct access for standalone play
    } else {
      // Set difficulty based on ESL game level using local JSON data
      if (spaceFootballState.game_level) {
        const displayLevel = parseInt(spaceFootballState.game_level);
        const levelData = getLevelData(displayLevel);
        
        if (levelData) {
          gameStateRef.current.difficulty = levelData.difficulty;
          
          // Update overlay title to show level
          setOverlayContent(prev => ({
            ...prev,
            title: `SPACE FOOTBALL - LEVEL ${displayLevel}`
          }));
        }
      }
    }
    
    initializeSounds();
    
    const canvas = canvasRef.current;
    if (canvas) {
      resizeCanvas();
      initializeGame();
    }

    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      
      // Cleanup game loop
      if (gameStateRef.current.animationFrame) {
        cancelAnimationFrame(gameStateRef.current.animationFrame);
      }
      
      // Cleanup timers
      if (gameStateRef.current.botServeTimer) {
        clearTimeout(gameStateRef.current.botServeTimer);
      }
    };
  }, [initializeSounds, resizeCanvas, initializeGame]);

  return (
    <div
      className={styles.gameContainer}
      style={isMobile()
        ? {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            overflow: 'hidden',
            touchAction: 'none',
            WebkitOverflowScrolling: 'auto',
          }
        : undefined}
    >
      <canvas
        ref={canvasRef}
        width="500"
        height="800"
        className={styles.gameCanvas}
        style={isMobile()
          ? {
              display: 'block',
              width: '100vw',
              height: '100vh',
              touchAction: 'none',
            }
          : undefined}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onClick={togglePause}
        aria-label="Space Football Game Canvas"
      />
      
      {showOverlay && (
        <div className={styles.overlay}>
          <h1 className={styles.titleText}>{overlayContent.title}</h1>
          {overlayContent.winner && (
            <h2 className={styles.winnerText}>{overlayContent.winner}</h2>
          )}
          {overlayContent.score && (
            <p className={styles.scoreText}>{overlayContent.score}</p>
          )}
          {countdown > 0 && (
            <h1 className={styles.countdown}>{countdown}</h1>
          )}
          <div className={styles.controls}>
            {overlayContent.showStart && (
              <button 
                className={styles.gameButton}
                onClick={startCountdown}
              >
                ▶ Start Game
              </button>
            )}
            {overlayContent.showRestart && (
              <button 
                className={styles.gameButton}
                onClick={restartGame}
              >
                Play Again
              </button>
            )}
          </div>
        </div>
      )}

      <MessagePopup
        isOpen={popupOpen}
        message={popupMessage}
        onClose={handlePopupClose}
      />
    </div>
  );
};

export default SpaceFootball;